import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import MaterialIcon from "../../../shared/components/MaterialIcon";
import EnterpriseFilterSelect from "../../../shared/components/EnterpriseFilterSelect";
import { DashboardMiniStat, DashboardPageHeader } from "../../../shared/components/dashboard/DashboardBento";
import { useTranslation } from "../../../i18n";
import { accountStatusLabel } from "../../../lib/accountStatus";
import { formatAdminError } from "../adminApi";
import {
  OFFER_STATUS,
  activateAdminOffer,
  deactivateAdminOffer,
  deleteAdminOffer,
  fetchAdminOfferCustomers,
  fetchAdminOfferOverview,
  fetchAdminOffers,
  formatOfferDate,
  formatOfferPrice,
} from "../adminOffersApi";
import AdminOfferEditorModal from "../components/AdminOfferEditorModal";

const STATUS_FILTERS = ["", "current", "draft", "paid", "disabled", "expired", "canceled", "replaced"];

const STATUS_TONE = {
  draft: "bg-slate-100 text-slate-700 ring-slate-200",
  active: "bg-secondary-container/60 text-on-secondary-container ring-secondary/30",
  paid: "bg-emerald-50 text-emerald-800 ring-emerald-200",
  canceled: "bg-rose-50 text-rose-800 ring-rose-200",
  expired: "bg-amber-50 text-amber-900 ring-amber-200",
  disabled: "bg-slate-100 text-slate-600 ring-slate-200",
  replaced: "bg-slate-50 text-slate-500 ring-slate-200",
};

export function OfferStatusBadge({ status, t }) {
  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wide ring-1 ring-inset ${
        STATUS_TONE[status] || STATUS_TONE.draft
      }`}
    >
      {t(`customOffer.status.${status}`)}
    </span>
  );
}

const TABLE_SELECT_BUTTON =
  "flex h-9 w-full items-center justify-between gap-2 rounded-lg border border-outline-variant/40 bg-surface-container-lowest px-2 text-body-md text-on-surface transition-all hover:ring-secondary/40 focus:outline-none focus:ring-2 focus:ring-secondary";

const AdminCustomOffersPage = () => {
  const { t, locale } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  const [tab, setTab] = useState("offers");
  const [overview, setOverview] = useState(null);
  const [error, setError] = useState("");
  const [flash, setFlash] = useState("");

  // offers tab
  const [offers, setOffers] = useState([]);
  const [offersCount, setOffersCount] = useState(0);
  const [offersLoading, setOffersLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [offerSearch, setOfferSearch] = useState("");
  const [userFilter, setUserFilter] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  // customers tab
  const [customers, setCustomers] = useState([]);
  const [customersCount, setCustomersCount] = useState(0);
  const [customersLoading, setCustomersLoading] = useState(false);
  const [customerSearch, setCustomerSearch] = useState("");
  const [hasOfferFilter, setHasOfferFilter] = useState("");

  // editor
  const [editor, setEditor] = useState({ open: false, offer: null, customer: null });

  const notify = (msg) => {
    setFlash(msg);
    window.setTimeout(() => setFlash(""), 3500);
  };

  const loadOverview = useCallback(async () => {
    try {
      setOverview(await fetchAdminOfferOverview());
    } catch {
      /* header stats are best-effort */
    }
  }, []);

  const loadOffers = useCallback(async () => {
    setOffersLoading(true);
    try {
      const data = await fetchAdminOffers({
        status: statusFilter || undefined,
        search: offerSearch.trim() || undefined,
        userId: userFilter?.id,
      });
      setOffers(data.results);
      setOffersCount(data.count);
      setError("");
    } catch {
      setError(t("adminOffers.loadError"));
    } finally {
      setOffersLoading(false);
    }
  }, [statusFilter, offerSearch, userFilter, t]);

  const loadCustomers = useCallback(async () => {
    setCustomersLoading(true);
    try {
      const data = await fetchAdminOfferCustomers({
        search: customerSearch.trim() || undefined,
        hasOffer: hasOfferFilter === "yes" ? true : hasOfferFilter === "no" ? false : undefined,
      });
      setCustomers(data.results);
      setCustomersCount(data.count);
      setError("");
    } catch {
      setError(t("adminOffers.loadError"));
    } finally {
      setCustomersLoading(false);
    }
  }, [customerSearch, hasOfferFilter, t]);

  useEffect(() => {
    loadOverview();
  }, [loadOverview]);

  useEffect(() => {
    const timer = window.setTimeout(loadOffers, offerSearch ? 350 : 0);
    return () => window.clearTimeout(timer);
  }, [loadOffers, offerSearch]);

  useEffect(() => {
    if (tab !== "customers") return undefined;
    const timer = window.setTimeout(loadCustomers, customerSearch ? 350 : 0);
    return () => window.clearTimeout(timer);
  }, [tab, loadCustomers, customerSearch]);

  // Deep link from the user detail page: ?user=<id> → offers of that customer,
  // or straight into the editor when they have none yet.
  useEffect(() => {
    const userId = searchParams.get("user");
    if (!userId) return;
    (async () => {
      try {
        const data = await fetchAdminOfferCustomers({ userId });
        const customer = data.results[0];
        if (!customer) return;
        if (customer.currentOffer) {
          setUserFilter({ id: customer.id, label: customer.fullName || customer.email });
          setStatusFilter("");
        } else {
          setEditor({ open: true, offer: null, customer });
        }
      } finally {
        const next = new URLSearchParams(searchParams);
        next.delete("user");
        setSearchParams(next, { replace: true });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refreshAll = async () => {
    await Promise.all([loadOffers(), loadOverview(), tab === "customers" ? loadCustomers() : null]);
  };

  const patchOffer = (updated) =>
    setOffers((prev) => prev.map((o) => (o.id === updated.id ? updated : o)));

  const runTransition = async (offer, fn, successKey) => {
    setBusyId(offer.id);
    setError("");
    try {
      const updated = await fn(offer.id);
      patchOffer(updated);
      notify(t(successKey, { reference: offer.reference }));
      await Promise.all([loadOverview(), loadOffers()]);
    } catch (err) {
      setError(formatAdminError(err));
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (offer) => {
    setBusyId(offer.id);
    setError("");
    try {
      await deleteAdminOffer(offer.id);
      setConfirmDeleteId(null);
      notify(t("adminOffers.deleted", { reference: offer.reference }));
      await refreshAll();
    } catch (err) {
      setError(formatAdminError(err));
    } finally {
      setBusyId(null);
    }
  };

  const hasOfferOptions = useMemo(
    () => [
      { value: "", label: t("adminOffers.hasOfferAll") },
      { value: "yes", label: t("adminOffers.hasOfferYes") },
      { value: "no", label: t("adminOffers.hasOfferNo") },
    ],
    [t]
  );

  const statusLabel = (value) =>
    value === "" ? t("adminOffers.filterAll") : value === "current" ? t("adminOffers.filterCurrent") : t(`customOffer.status.${value}`);

  const openCreate = (customer = null) => setEditor({ open: true, offer: null, customer });
  const openEdit = (offer) => setEditor({ open: true, offer, customer: offer.customer });

  return (
    <div className="bg-background font-body-md text-on-surface antialiased">
      <div className="mx-auto min-h-screen max-w-container-max px-margin-mobile py-stack-lg md:px-margin-desktop">
        <DashboardPageHeader
          eyebrow={t("adminOffers.eyebrow")}
          title={t("adminOffers.title")}
          subtitle={t("adminOffers.subtitle")}
          actions={
            <button
              type="button"
              onClick={() => openCreate()}
              className="inline-flex items-center gap-2 rounded-full bg-secondary px-6 py-2.5 text-[14px] font-bold text-white shadow-[0_6px_20px_rgba(0,106,97,0.3)] transition hover:opacity-90"
            >
              <MaterialIcon name="add" className="text-[18px]" />
              {t("adminOffers.newOffer")}
            </button>
          }
        />

        <div className="mb-stack-lg grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <DashboardMiniStat label={t("adminOffers.stats.live")} value={overview?.byStatus?.active ?? 0} icon="workspace_premium" highlight locale={locale} loading={!overview} />
          <DashboardMiniStat label={t("adminOffers.stats.paid")} value={overview?.byStatus?.paid ?? 0} icon="task_alt" locale={locale} loading={!overview} />
          <DashboardMiniStat label={t("adminOffers.stats.withOffer")} value={overview?.customersWithOffer ?? 0} icon="group" locale={locale} loading={!overview} />
          <DashboardMiniStat label={t("adminOffers.stats.withoutOffer")} value={overview?.customersWithoutOffer ?? 0} icon="person_off" locale={locale} loading={!overview} />
        </div>

        <div className="mb-stack-md flex flex-wrap items-center gap-3">
          <div className="flex rounded-full border border-outline-variant/40 bg-surface-container-lowest p-1">
            {["offers", "customers"].map((value) => (
              <button
                key={value}
                type="button"
                onClick={() => setTab(value)}
                className={`rounded-full px-5 py-1.5 font-label-sm font-semibold transition-colors ${
                  tab === value ? "bg-primary text-on-primary" : "text-on-surface-variant hover:text-on-surface"
                }`}
              >
                {value === "offers" ? t("adminOffers.tabOffers") : t("adminOffers.tabCustomers")}
              </button>
            ))}
          </div>
        </div>

        {flash ? (
          <p className="mb-4 flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-[14px] text-emerald-900" role="status">
            <MaterialIcon name="check_circle" filled className="text-[18px] text-emerald-600" />
            {flash}
          </p>
        ) : null}
        {error ? (
          <p className="mb-4 rounded-xl border border-error/30 bg-error-container px-4 py-3 text-[14px] text-on-error-container">
            {error}
          </p>
        ) : null}

        {tab === "offers" ? (
          <>
            <div className="mb-4 flex flex-wrap items-center gap-3">
              <div className="flex flex-wrap rounded-full border border-outline-variant/40 bg-surface-container-lowest p-1">
                {STATUS_FILTERS.map((value) => (
                  <button
                    key={value || "all"}
                    type="button"
                    onClick={() => setStatusFilter(value)}
                    className={`rounded-full px-3.5 py-1.5 font-label-sm font-medium transition-colors ${
                      statusFilter === value ? "bg-primary text-on-primary" : "text-on-surface-variant hover:text-on-surface"
                    }`}
                  >
                    {statusLabel(value)}
                  </button>
                ))}
              </div>
              <input
                type="search"
                value={offerSearch}
                onChange={(e) => setOfferSearch(e.target.value)}
                placeholder={t("adminOffers.searchOffers")}
                className="w-full max-w-xs rounded-xl border border-outline-variant bg-surface-container-lowest px-4 py-2.5 outline-none focus:ring-2 focus:ring-secondary"
              />
              {userFilter ? (
                <button
                  type="button"
                  onClick={() => setUserFilter(null)}
                  className="inline-flex items-center gap-1.5 rounded-full bg-secondary/10 px-3 py-1.5 text-[12.5px] font-semibold text-secondary ring-1 ring-secondary/30"
                >
                  <MaterialIcon name="person" className="text-[16px]" />
                  {userFilter.label}
                  <MaterialIcon name="close" className="text-[14px]" />
                </button>
              ) : null}
            </div>

            {offersLoading && offers.length === 0 ? (
              <div className="space-y-3">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="h-[72px] animate-pulse rounded-xl bg-surface-container" />
                ))}
              </div>
            ) : offers.length === 0 ? (
              <div className="tonal-card rounded-2xl border border-dashed border-outline-variant px-6 py-14 text-center">
                <MaterialIcon name="workspace_premium" className="text-[40px] text-outline-variant" />
                <p className="mt-3 font-headline-md text-primary">{t("adminOffers.noOffers")}</p>
                <button
                  type="button"
                  onClick={() => openCreate()}
                  className="mt-4 inline-flex items-center gap-2 rounded-full border border-secondary/40 px-5 py-2 text-[13.5px] font-semibold text-secondary transition hover:bg-secondary/5"
                >
                  <MaterialIcon name="add" className="text-[16px]" />
                  {t("adminOffers.newOffer")}
                </button>
              </div>
            ) : (
              <ul className="space-y-3">
                {offers.map((offer) => {
                  const expanded = expandedId === offer.id;
                  const busy = busyId === offer.id;
                  const canActivate = ![OFFER_STATUS.ACTIVE, OFFER_STATUS.PAID].includes(offer.status);
                  const canDeactivate = offer.status === OFFER_STATUS.ACTIVE;
                  const locked = offer.status === OFFER_STATUS.PAID;
                  return (
                    <li key={offer.id} className="tonal-card overflow-hidden rounded-xl border border-outline-variant/30">
                      <button
                        type="button"
                        onClick={() => setExpandedId(expanded ? null : offer.id)}
                        className="flex w-full flex-wrap items-center justify-between gap-3 px-4 py-3 text-left"
                      >
                        <span className="flex min-w-0 items-center gap-3">
                          <MaterialIcon name={expanded ? "expand_more" : "chevron_right"} className="shrink-0 text-[18px] text-on-surface-variant" />
                          <span className="min-w-0">
                            <span className="flex flex-wrap items-center gap-2">
                              <span className="font-mono text-[12px] text-on-surface-variant">{offer.reference}</span>
                              <span className="truncate font-semibold text-primary">{offer.title}</span>
                              {offer.version > 1 ? (
                                <span className="rounded bg-surface-container-high px-1.5 py-0.5 text-[10px] font-bold text-on-surface-variant">
                                  {t("adminOffers.version", { version: offer.version })}
                                </span>
                              ) : null}
                            </span>
                            {offer.customer ? (
                              <span className="mt-0.5 block truncate text-[12.5px] text-on-surface-variant">
                                {offer.customer.fullName || "—"} · {offer.customer.email}
                                {offer.customer.companyName ? ` · ${offer.customer.companyName}` : ""}
                              </span>
                            ) : null}
                          </span>
                        </span>
                        <span className="flex shrink-0 items-center gap-4">
                          <span className="text-right">
                            <span className="block font-headline-md text-[16px] font-bold tabular-nums text-primary">
                              {formatOfferPrice(offer.price, locale)}
                            </span>
                            <span className="block text-[11px] text-on-surface-variant">
                              {t(`customOffer.interval.${offer.billingInterval}`)}
                            </span>
                          </span>
                          <OfferStatusBadge status={offer.status} t={t} />
                        </span>
                      </button>

                      {expanded ? (
                        <div className="border-t border-outline-variant/30 bg-surface-container-low/40 p-4">
                          <div className="grid gap-5 md:grid-cols-[1.2fr_1fr]">
                            <div className="space-y-4">
                              {offer.description ? (
                                <p className="whitespace-pre-line text-[13.5px] leading-relaxed text-on-surface">{offer.description}</p>
                              ) : null}
                              {offer.features.length ? (
                                <ul className="grid gap-1.5 sm:grid-cols-2">
                                  {offer.features.map((f, i) => (
                                    <li key={i} className="flex items-start gap-2 text-[13px]">
                                      <MaterialIcon name={f.included ? "check_circle" : "cancel"} filled={f.included} className={f.included ? "text-[16px] text-secondary" : "text-[16px] text-outline-variant"} />
                                      <span className={f.included ? "" : "text-outline line-through"}>{f.label}</span>
                                    </li>
                                  ))}
                                </ul>
                              ) : null}
                              {offer.limits.length ? (
                                <dl className="flex flex-wrap gap-2">
                                  {offer.limits.map((l, i) => (
                                    <div key={i} className="rounded-lg border border-outline-variant/30 bg-white px-3 py-1.5 text-[12.5px]">
                                      <dt className="inline text-on-surface-variant">{l.label}: </dt>
                                      <dd className="inline font-semibold text-primary">{l.value}</dd>
                                    </div>
                                  ))}
                                </dl>
                              ) : null}
                              {offer.adminNotes ? (
                                <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[12.5px] text-amber-900">
                                  <span className="font-semibold">{t("adminOffers.notes")}: </span>
                                  {offer.adminNotes}
                                </p>
                              ) : null}
                            </div>
                            <div className="space-y-1.5 text-[12.5px] text-on-surface-variant">
                              <p>{t("adminOffers.colValidity")}: <span className="font-semibold text-primary">{offer.validUntil ? formatOfferDate(offer.validUntil, locale, true) : "—"}</span></p>
                              <p>{t("adminOffers.colUpdated")}: <span className="font-semibold text-primary">{formatOfferDate(offer.updatedAt, locale, true)}</span></p>
                              {offer.activatedAt ? <p>{t("adminOffers.sentOn", { date: formatOfferDate(offer.activatedAt, locale, true) })}</p> : null}
                              {offer.paidAt ? <p className="font-semibold text-emerald-800">{t("adminOffers.paidOn", { date: formatOfferDate(offer.paidAt, locale, true) })}</p> : null}
                              {offer.canceledAt ? <p className="font-semibold text-rose-800">{t("adminOffers.canceledOn", { date: formatOfferDate(offer.canceledAt, locale, true) })}</p> : null}
                              {offer.stripeSubscriptionId ? <p className="font-mono text-[11px]">{offer.stripeSubscriptionId}</p> : null}
                              {offer.createdByName ? <p>{t("adminOffers.createdBy", { name: offer.createdByName })}</p> : null}
                              {offer.customer ? (
                                <Link to={`/dashboard/admin/users/${offer.customer.id}`} className="inline-flex items-center gap-1 font-semibold text-secondary hover:underline">
                                  {t("adminOffers.openCustomer")}
                                  <MaterialIcon name="arrow_forward" className="text-[14px]" />
                                </Link>
                              ) : null}
                            </div>
                          </div>

                          <div className="mt-4 flex flex-wrap items-center justify-end gap-2 border-t border-outline-variant/20 pt-4">
                            {!locked ? (
                              <button type="button" disabled={busy} onClick={() => openEdit(offer)} className="inline-flex items-center gap-1.5 rounded-full border border-outline-variant/50 bg-white px-4 py-2 text-[13px] font-semibold text-primary transition hover:border-secondary/40 disabled:opacity-50">
                                <MaterialIcon name="edit" className="text-[16px]" />
                                {t("adminOffers.edit")}
                              </button>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-[12.5px] text-on-surface-variant">
                                <MaterialIcon name="lock" className="text-[14px]" />
                                {t("adminOffers.locked")}
                              </span>
                            )}
                            {canActivate ? (
                              <button type="button" disabled={busy} onClick={() => runTransition(offer, activateAdminOffer, "adminOffers.activated")} className="inline-flex items-center gap-1.5 rounded-full bg-secondary px-4 py-2 text-[13px] font-bold text-white transition hover:opacity-90 disabled:opacity-50">
                                <MaterialIcon name="send" className="text-[16px]" />
                                {t("adminOffers.activate")}
                              </button>
                            ) : null}
                            {canDeactivate ? (
                              <button type="button" disabled={busy} onClick={() => runTransition(offer, deactivateAdminOffer, "adminOffers.deactivated")} className="inline-flex items-center gap-1.5 rounded-full border border-outline-variant/50 bg-white px-4 py-2 text-[13px] font-semibold text-on-surface-variant transition hover:text-primary disabled:opacity-50">
                                <MaterialIcon name="visibility_off" className="text-[16px]" />
                                {t("adminOffers.deactivate")}
                              </button>
                            ) : null}
                            {!locked ? (
                              confirmDeleteId === offer.id ? (
                                <span className="inline-flex items-center gap-2 rounded-full bg-error-container px-3 py-1.5 text-[12.5px] text-on-error-container">
                                  {t("adminOffers.confirmDelete")}
                                  <button type="button" disabled={busy} onClick={() => handleDelete(offer)} className="font-bold underline">
                                    {t("adminOffers.yes")}
                                  </button>
                                  <button type="button" onClick={() => setConfirmDeleteId(null)} className="font-semibold">
                                    {t("adminOffers.no")}
                                  </button>
                                </span>
                              ) : (
                                <button type="button" disabled={busy} onClick={() => setConfirmDeleteId(offer.id)} className="inline-flex items-center gap-1.5 rounded-full px-3 py-2 text-[13px] font-semibold text-on-surface-variant transition hover:text-error disabled:opacity-50">
                                  <MaterialIcon name="delete" className="text-[16px]" />
                                  {t("adminOffers.delete")}
                                </button>
                              )
                            ) : null}
                          </div>
                        </div>
                      ) : null}
                    </li>
                  );
                })}
              </ul>
            )}
            {offersCount > offers.length ? (
              <p className="mt-4 font-label-sm text-on-surface-variant">{offers.length} / {offersCount}</p>
            ) : null}
          </>
        ) : (
          <>
            <div className="mb-4 flex flex-wrap items-center gap-3">
              <input
                type="search"
                value={customerSearch}
                onChange={(e) => setCustomerSearch(e.target.value)}
                placeholder={t("adminOffers.searchCustomers")}
                className="w-full max-w-xs rounded-xl border border-outline-variant bg-surface-container-lowest px-4 py-2.5 outline-none focus:ring-2 focus:ring-secondary"
              />
              <EnterpriseFilterSelect
                value={hasOfferFilter}
                onChange={setHasOfferFilter}
                options={hasOfferOptions}
                minWidth="190px"
                fullWidth={false}
                buttonClassName={TABLE_SELECT_BUTTON}
                ariaLabel={t("adminOffers.hasOfferAll")}
              />
            </div>

            {customersLoading && customers.length === 0 ? (
              <div className="space-y-3">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="h-[64px] animate-pulse rounded-xl bg-surface-container" />
                ))}
              </div>
            ) : (
              <div className="overflow-hidden rounded-2xl border border-outline-variant/30 bg-surface-container-lowest shadow-soft">
                <div className="overflow-x-auto">
                  <table className="min-w-full whitespace-nowrap text-left text-body-md">
                    <thead className="border-b border-outline-variant/30 bg-surface-container-low text-label-sm font-semibold uppercase text-on-surface-variant">
                      <tr>
                        <th className="px-4 py-3">{t("adminOffers.colCustomer")}</th>
                        <th className="px-4 py-3">{t("adminOffers.colPlan")}</th>
                        <th className="px-4 py-3">{t("adminOffers.colCurrentOffer")}</th>
                        <th className="px-4 py-3 text-right">{t("adminOffers.colActions")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {customers.map((c) => (
                        <tr key={c.id} className="border-b border-outline-variant/20 last:border-0">
                          <td className="px-4 py-3">
                            <Link to={`/dashboard/admin/users/${c.id}`} className="font-medium text-primary hover:text-secondary hover:underline">
                              {c.fullName || c.email}
                            </Link>
                            <span className="block text-[12.5px] text-on-surface-variant">
                              {c.email}
                              {c.companyName ? ` · ${c.companyName}` : ""}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-on-surface-variant">{accountStatusLabel(c.accountStatus, t)}</td>
                          <td className="px-4 py-3">
                            {c.currentOffer ? (
                              <span className="inline-flex flex-wrap items-center gap-2">
                                <OfferStatusBadge status={c.currentOffer.status} t={t} />
                                <span className="font-medium text-primary">{c.currentOffer.title}</span>
                                <span className="text-[12.5px] text-on-surface-variant">
                                  {formatOfferPrice(c.currentOffer.price, locale)} {t(`customOffer.interval.${c.currentOffer.billingInterval}`)}
                                </span>
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1.5 text-[13px] text-outline">
                                <MaterialIcon name="remove_circle_outline" className="text-[16px]" />
                                {t("adminOffers.noOffer")}
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-right">
                            {c.currentOffer ? (
                              <button
                                type="button"
                                onClick={() => {
                                  setUserFilter({ id: c.id, label: c.fullName || c.email });
                                  setStatusFilter("");
                                  setTab("offers");
                                }}
                                className="rounded-lg border border-outline-variant/50 bg-white px-3 py-1.5 text-label-sm font-semibold text-primary transition hover:border-secondary/40"
                              >
                                {t("adminOffers.openOffer")}
                              </button>
                            ) : (
                              <button
                                type="button"
                                onClick={() => openCreate(c)}
                                className="rounded-lg border border-secondary/30 bg-secondary/5 px-3 py-1.5 text-label-sm font-semibold text-secondary transition hover:bg-secondary/10"
                              >
                                {t("adminOffers.createFor")}
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {customers.length === 0 ? (
                  <p className="px-4 py-10 text-center text-[14px] text-on-surface-variant">{t("adminOffers.noCustomers")}</p>
                ) : null}
                {customersCount > customers.length ? (
                  <p className="border-t border-outline-variant/20 px-4 py-3 font-label-sm text-on-surface-variant">{customers.length} / {customersCount}</p>
                ) : null}
              </div>
            )}
          </>
        )}
      </div>

      <AdminOfferEditorModal
        open={editor.open}
        offer={editor.offer}
        customer={editor.customer}
        onClose={() => setEditor({ open: false, offer: null, customer: null })}
        onSaved={async (saved) => {
          setEditor({ open: false, offer: null, customer: null });
          notify(
            editor.offer
              ? t("adminOffers.saved", { reference: saved.reference })
              : saved.status === OFFER_STATUS.ACTIVE
                ? t("adminOffers.createdAndSent", { reference: saved.reference })
                : t("adminOffers.created", { reference: saved.reference })
          );
          setTab("offers");
          setExpandedId(saved.id);
          await refreshAll();
        }}
      />
    </div>
  );
};

export default AdminCustomOffersPage;
